import {
	Button,
	Stack,
	Typography,
	Box,
} from '@mui/material';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import RowTypography from 'components/Table/RowTypography';
import React, { useEffect, useState} from 'react';

import { ReactComponent as ResourceMapping } from '../../../../src/assets/resourceMappingNew.svg';
import styled from '@emotion/styled';
import {ReactComponent as WarningImage} from '../../../assets/warning.svg';
import { CellExpanderFormatter } from 'components/cell_expander_formatter';
import moment from 'moment';
import { useTheme } from "@mui/material/styles";

const StyledConfirmationIcon = styled(ResourceMapping)({
	width: '5rem',
	height: '5rem'
});

type ModalWidth = "sm" | "md" | "lg" | "xl";

interface ResourceAllocationSuccessModalProps {
    onTitleChange: (newtitle: string) => void;
    onWidthChange: (newWidth: ModalWidth) => void;
    onCancel: () => void;
    modalTitle: string;
    setIsSuccessModalOpen: any;
    allocationResponse: any;
    errorResponse: any;
	allocationType: {
		key: string;
		label: string;
	};
}

interface ResourceColumnOptions {
	row: any;
	column: any;
	isCellSelected: boolean;
  	toggleSubRow: (id: string, resourceType: any) => void;
}
		
const ResourceCell = ({row, column, isCellSelected, toggleSubRow}: ResourceColumnOptions) => {
	const cellValue = row[column.key];

	let groupCellValue = '';
	if (Array.isArray(cellValue) && cellValue.length > 1) {
		const validCount = cellValue.filter((item: any) => item).length;
		if (validCount > 1) {
			groupCellValue = `${validCount} ${column.name}s`;
		} else {
			groupCellValue = cellValue.find((item: any) => item) || '';
		}
	} else {
		groupCellValue = cellValue || '';
	}

	const displayValue =
		column.key === 'allocation'
			? `${cellValue ?? 0}`
			: cellValue;
	return (
		<Stack direction="row" alignItems="center" spacing={1}>
			{column.key === 'resource' && !!row.values && row.values.length > 0 && (
					<CellExpanderFormatter
						isCellSelected={isCellSelected}
						expanded={row.isExpanded === true}
						onCellExpand={() => {
							toggleSubRow(row.id, row.values);
						}}
					/>
				)}
			<RowTypography isSuccessor={!row[column.key]} isEditing>
				{column.key === 'sector' || column.key === 'project' || column.key === 'program' ? (
					<Stack direction="row" spacing={1}>
					{
						<Box
							sx={{
								backgroundColor: '#299BFF26 !important',
								color: '#003771 !important',
								px: 1,
								borderRadius: 3
							}}
						>
							{groupCellValue}
						</Box>
					}
				</Stack>
				) : (displayValue)}
			</RowTypography>
		</Stack>
	);
};

const ResourceAllocationSuccessModal: React.FC<ResourceAllocationSuccessModalProps> = ({ onTitleChange, onWidthChange, modalTitle, onCancel, setIsSuccessModalOpen, allocationResponse, allocationType, errorResponse }) => {
	const theme= useTheme();
	const colors = theme.palette;
	const defaultColumnNames = ['Resource', 'Sector', 'Environment', 'Program', 'Project', 'Allocation%', 'Start Date', 'End Date'];

	const columnNames = [
		{ name: 'sector', label: 'Sector' },
		{ name: 'environment', label: 'Environment' },
		{ name: 'program', label: 'Program' },
		{ name: 'project', label: 'Project' },
		{ name: 'allocation', label: 'Allocation%' },
		{ name: 'startDate', label: 'Start Date' },
		{ name: 'endDate', label: 'End Date' }
	];

	const [rows, setRows] = useState<any>([]);
	
	useEffect(() => {
		if (allocationResponse) {
			const responseData = allocationResponse ?? [];
			const data = {
				sectors: new Set(),
				projects: new Set(),
				programs: new Set(),
				allocations: [] as number[],
				startDate: String(),
				endDate: String()
			};

			if (Array.isArray(allocationResponse) && allocationResponse.length > 0) {
				responseData?.forEach((item: any) => {
					data.sectors.add(item.sector);
					data.projects.add(item.project);
					data.programs.add(item.program);
					data.allocations.push(item.allocation);
					data.startDate = item.startDate;
					data.endDate = item.endDate;
				});

				const allocationValues = data.allocations.map((perc) => `${perc}%`).join(', ');
				setRows([
					{
						id: crypto.randomUUID(),
						resource: responseData[0]?.resource,
						sector: Array.from(data.sectors),
						program: Array.from(data.programs),
						project: Array.from(data.projects),
						environment: responseData[0].environment,
						allocation: `(${allocationValues})`,
						values: responseData?.map((res: any) => ({
							...res,
							id: crypto.randomUUID(),
							startDate: moment(res.startDate).format('YYYY-MM-DD'),
							endDate:
								res.endDate === '9999-12-31' || res.endDate === ''
									? moment().format('YYYY-MM-DD')
									: moment(res.endDate).format('YYYY-MM-DD')
						}))
					}
				]);
			}
		}
	}, [allocationResponse]);

	const handleCancel = () => {
		setIsSuccessModalOpen(false);
		onCancel();
	};

	function toggleSubRow(id: string, resourceTypes: any) {
		setRows((prev: any) => {
			const rows = [...prev];
			const rowIndex = rows.findIndex((r: any) => r.id === id);
			const row = rows[rowIndex];
			const newRows = [...rows];
			let children: any[] = [];
			let childRows: any[] = [];
			if (!row.isExpanded) {
				if (resourceTypes?.length > 0) {
					childRows = resourceTypes;
				}
				children = childRows.map((x: any) => ({
					...x,
					isChildren: true
				}));
				newRows.splice(Number(rowIndex) + 1, 0, ...children);
				newRows[rowIndex] = { ...row, isExpanded: true, children };

				row.isExpanded = true;
			} else {
				children = row.children ?? [];
				newRows.splice(Number(rowIndex) + 1, children.length);
				newRows[rowIndex] = { ...row, isExpanded: false, children: [] };

				row.isExpanded = false;
			}
			return newRows;
		});
	}

	const resourceFormatter = () => {
		const resourceCell = ({ row, column, isCellSelected }: any) => (
			<ResourceCell
				row={row}
				column={column}
				isCellSelected={isCellSelected}
				toggleSubRow={toggleSubRow}
			/>
		);
		return resourceCell;
	};
	
	function getColumns(): Array<any> {
		return [
			{
				name: 'resource',
				label: 'Resource'
			},
			...columnNames
		].map((col: any, idx: number) => {
			let columnWidth = 145;
			if ([allocationType.key].includes(col.name)) {
				columnWidth = 230;
			} else if (['allocation'].includes(col.name)) {
				columnWidth = 200;
			}

			return {
				key: col.name,
				name: col.label,
				editable: () => col.isEditable,
				formatter: resourceFormatter(),
				width: columnWidth 
			};
		});
	}

	return (
		<Stack sx={{backgroundColor: `${colors.modal.bg} !important`, color: `${colors.modal.text} !important`}}>
			{errorResponse?.error && (
				<Stack
					direction="row"
					py="0.5rem"
					px="1.5rem"
					sx={{ backgroundColor: 'boxColor.orange.bgColor', display: 'flex', alignItems: 'center' }}
				>
					<WarningImage className="warning-image" />
					<Typography variant="tableColumnFont" sx={{ color: `${colors.modal.text} !important`, ml: '0.5rem' }}>
					{errorResponse?.error && (
						<>
							{errorResponse?.error}{' '}
						</>
					)} 
					</Typography>
					{errorResponse?.detail?.missedDates &&
						errorResponse.detail.missedDates.length > 0 &&
						errorResponse.detail.missedDates.map((dates: any, index: any) => (
							<Typography key={`missed-${dates?.prevEndDate ?? ''}-${index}`} variant="tableColumnFont" sx={{ color: `${colors.modal.text} !important`, ml: '0.5rem' }}>
								<br />
								Prev. End Date: {new Date(dates?.prevEndDate).toLocaleDateString()}
								<br />
								{dates?.currentStartDate ? 'Current Start Date:' : 'Current Date:'}
								{' '} 
								{dates?.currentStartDate ? new Date(dates?.currentStartDate).toLocaleDateString() : new Date(dates?.currentEndDate).toLocaleDateString()}
								<br />
								No. of Days:{' '}{dates?.NoofDays}
								<br />
							</Typography>
					))}
				</Stack>
			)}
			<Stack sx={{ padding: '15px' }}>
				<Stack sx={{ flexDirection: 'row', gap: 2 }}>
					<Box sx={{ alignItems: 'center', justifyContent: 'flex-start', width: '5rem' }}>
						<StyledConfirmationIcon />
					</Box>
					<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
						<Typography variant="h6" sx={{ fontWeight: 500 }}>
						{(typeof allocationResponse !== 'string') ? `Resource Allocation Successful.` : allocationResponse}
						</Typography>
                        <Typography sx={{ alignSelf: 'flex-start' }}>
						{(typeof allocationResponse !== 'string') ? `1 Resource is allocated successfully` : ''}
							</Typography>
					</Box>
				</Stack>
				<Stack sx={{ flexDirection: 'column', gap: 3, textAlign: 'left' }}>
				{(Array.isArray(allocationResponse) && allocationResponse .length > 0) || (typeof allocationResponse !== 'string') ? (
					<CustomizedDataGrid
						tableColumns={getColumns()}
						tableData={rows}
						defaultColumnNames={defaultColumnNames}
						autoSetDraggedColumns={false}
						isRenderPagination={false}
						isShowMoreButton={false}
						style={{ maxHeight: '250px' }}
					/>
				) : null}
					<Stack direction="row" gap={2} sx={{ alignSelf: 'flex-end' }}>
						<Button variant="pepwiseSecondary" onClick={handleCancel}>CLOSE</Button>
					</Stack>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default ResourceAllocationSuccessModal;
